import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, UserPlus, MessageCircle, Coins, PartyPopper } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import ThankYouModal from "./ThankYouModal";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now"; if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`; return `${Math.floor(d / 86400)}d`;
}

const ICONS = { follow: UserPlus, tip: Coins, media_like: Heart, media_comment: MessageCircle, thank_you: Heart, goal_reached: PartyPopper };

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [thankTarget, setThankTarget] = useState(null);
  const navigate = useNavigate();

  const loadCount = useCallback(() => api.get("/notifications/unread_count").then((r) => setCount(r.data.count)).catch(() => {}), []);
  useEffect(() => { loadCount(); const i = setInterval(loadCount, 20000); return () => clearInterval(i); }, [loadCount]);

  const onOpen = async (o) => {
    setOpen(o);
    if (o) {
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
        await api.post("/notifications/read");
        setCount(0);
      } catch {}
    }
  };

  const label = (n) => {
    if (n.type === "tip") return `tipped you $${(n.amount || 0).toFixed(2)}`;
    if (n.type === "thank_you") return `sent you a thank-you: "${n.message}"`;
    return n.message;
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-full hover:bg-secondary text-foreground/70">
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 bg-copper text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl p-0 overflow-hidden" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-border font-serif font-semibold text-bronze">Notifications</div>
        <div className="max-h-96 overflow-y-auto no-scrollbar">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">No notifications yet 💛</p>
          ) : items.map((n) => {
            const Ic = ICONS[n.type] || Bell;
            const canThank = n.actor_id && ["follow", "tip", "media_like", "media_comment"].includes(n.type);
            return (
              <div key={n.notif_id} className={`flex items-center gap-2 px-4 py-3 hover:bg-secondary ${!n.read ? "bg-copper/5" : ""}`}>
                <button onClick={() => { if (n.actor_id) navigate(`/u/${n.actor_id}`); setOpen(false); }}
                  className="flex items-center gap-3 text-left flex-1 min-w-0">
                  <div className="relative shrink-0">
                    <img src={fileUrl(n.actor_avatar) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${n.actor_id || "x"}`} alt="" className="w-9 h-9 rounded-full bg-secondary" />
                    <span className="absolute -bottom-1 -right-1 bg-copper text-white rounded-full p-0.5"><Ic className="w-2.5 h-2.5" /></span>
                  </div>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="truncate"><span className="font-semibold">{n.actor_name}</span> {label(n)}</p>
                    <p className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</p>
                  </div>
                </button>
                {canThank && (
                  <button data-testid="notif-thank-btn" onClick={() => setThankTarget({ user_id: n.actor_id, name: n.actor_name })}
                    className="shrink-0 text-xs font-semibold text-copper border border-copper/40 rounded-full px-2.5 py-1 hover:bg-copper/10">
                    Say thanks
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
      <ThankYouModal recipient={thankTarget} onClose={() => setThankTarget(null)} />
    </Popover>
  );
}
